import React, { Component } from 'react'
import { Col, Container, Row, Form } from 'react-bootstrap';
import { Button } from "@mui/material";
import Select from '../../Components/PCSelect';
import WeekRangeSelect from '../../Components/WeekRangeSelect';
import axios from "axios";

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Summary from './Summary'
import ByWeek from './ByWeek'
import ByShop from './ByShop'
import { getISOWeek, getWeekDetails, round2, normalizeWeekCode } from './../../helper';
import PageHeader from '../../Pages/admin/shared/PageHeader';
import PrintButton from '../../Pages/admin/shared/PrintButton';
import FilterCard from '../../Pages/admin/shared/FilterCard';
import { myEntities, ownLimit } from '../../helper/identity';

class ProfitAndLoss extends Component {
    constructor(props) {
        super(props);
        this.state = {

            Shop_Name: [],
            // Default the filter to the current week so the dropdown opens on it
            // instead of the oldest listed week. The report still waits for "View".
            From_Week: getWeekDetails().weekId,
            To_Week: getWeekDetails().weekId,
            // reportView: "summary",
            reportView: "by_shop",

            ShopList: [],
            AllShopData: [],
            shopPaymentDays: [],
            showReport: false,

            ReportDetails: [],
            unapprovedWeeks: [],

        }
    }

    componentDidMount() {
        this.getShopList()
    }

    async getShopList() {
        let ShopList = []

        /* Only the shops the user may see (user, 2026-11-02): every shop for an admin; otherwise
         * their own shop, or their pay account's partner shops (helper/identity.js ownLimit).
         * Exactly one → selected and locked. Several partner shops: the account user picks. */
        const limit = ownLimit('shop', '/report/profit-loss', true);
        if (limit) {
            const isShopUser = myEntities('shop', '/report/profit-loss').length > 0;
            ShopList = limit.map(s => ({ value: String(s.id), label: s.name || `Shop ${s.id}` }));
            const shopLocked = ShopList.length === 1;
            this.setState({ ShopList, Shop_Name: (shopLocked || isShopUser) ? ShopList : [], shopLocked });
            return;
        } else {
            const getShops = await axios.get(`${process.env.REACT_APP_BASE_URL}services/query/PCGetShops.php`);
            let shopData = getShops.data
            if (shopData.success === 1) {

                for (let i = 0; i < shopData.data.length; i++) {
                    const shop = shopData.data[i];
                    ShopList.push({
                        /* String(), because pc_shop.PC_SP_ID is an `int` column while EVERY shop
                           id this report compares it against is varchar — pc_weekly_sale.
                           PC_WS_SHOPID(50), shop_expense_master.SHOP_ID(45), pc_week_del_bd.
                           BD_TO(100), pc_acc_from_entries.FROM_SHOP_ID(20), shop_payment_days.
                           payTo_id(45). PDO hands back an int for the int column and strings for
                           the rest, so `3 !== '3'` skipped every row and all three views came up
                           empty. The own-shops branch above uses String ids too. */
                        value: String(shop.PC_SP_ID),
                        label: shop.SP_NAME
                    })
                }

            }
        }
        this.setState({ ShopList })
    }

    async handleChange(fieldName, evnt) {
        let targetValue = ""
        switch (fieldName) {
            case 'Shop_Name':
                targetValue = evnt
                break;
            default:
                targetValue = evnt.target.value
                break;
        }

        this.setState({ [fieldName]: targetValue, showReport: false })
    }

    /* The week's expenses for ONE shop, as both a list to print and a breakdown to total.
     *
     * Single source of truth for all three views. Two rules live here and nowhere else:
     *
     *   1. Charges (rent / K&N / NDR) are matched on shop AND week, because the backend returns
     *      one row per (shop, week) carrying the rate that was effective for that week. Matching
     *      on shop alone re-adds every other week's charges — the bug the old By Week and Summary
     *      code had.
     *   2. Delivery comes from pc_week_del_bd, aliased by the backend. It is NOT
     *      pc_shop_trans_cash TR_TYPE='4', which holds zero rows.
     *
     * Pay-by-account and shop-payment lines are ITEMISED from the detail arrays added to
     * profitAndLoss.php on 2026-10-13. If those arrays are absent — an older backend, or a
     * frontend deployed ahead of it — each falls back to the single summed line it used before,
     * so the report still totals correctly rather than silently dropping the expense.
     */
    /* Shop ids arrive as an int from pc_shop (an `int` column) and as strings from every other
     * table (all varchar). Compare them as strings so a strict === cannot silently drop every
     * row — which is exactly what happened on 2026-10-14 after a fresh database was connected. */
    static sameShop(a, b) {
        return String(a) === String(b);
    }

    buildWeekExpenses(shopId, week, src) {
        const lines = [];
        let total = 0;
        const breakdown = { rent: 0, kn: 0, ndr: 0, delivery: 0, payAcc: 0, shopPay: 0, shopTrans: 0 };
        const add = (description, amount, bucket) => {
            const n = +amount || 0;
            total += n;
            breakdown[bucket] += n;
            // `bucket` lets the Summary list a shop's "Other" lines (T1.36); By Week / By Shop ignore it.
            lines.push({ description, amount, bucket });
        };

        // 1. Effective-dated shop charges — matched on shop AND week.
        for (const r of src.charges) {
            if (ProfitAndLoss.sameShop(shopId, r.PC_SP_ID) && r.PC_WS_WEEK === week) {
                add('Rent Charges', r.SP_RENT_CHARGES, 'rent');
                add('K & N Charges', r.SP_KN_CHARGES, 'kn');
                add('NDR Charges', r.SP_NDR_CHARGES, 'ndr');
            }
        }

        // 2. Delivery — one line; pc_week_del_bd carries no description, only a source warehouse.
        for (const r of src.delivery) {
            if (ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week) add('Weekly Delivery', r.TR_AMT, 'delivery');
        }

        // 3. Pay by account — itemised, with what it was for and who charged it.
        const payAcc = src.payAccDetail.filter(r => ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week);
        if (payAcc.length > 0) {
            payAcc.forEach(r => add(
                (r.TR_DET && String(r.TR_DET).trim() !== '' ? r.TR_DET : 'Pay By Account')
                + (r.ACCOUNT_TITLE ? ' — ' + r.ACCOUNT_TITLE : ''),
                r.TR_AMT, 'payAcc'));
        } else {
            for (const r of src.payAccSummed) {
                if (ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week) add('Pay By Account', r.TR_AMT, 'payAcc');
            }
        }

        // 4. pc_shop_trans_cash TR_TYPE='3' — already carried its own description.
        for (const r of src.shopTransCash) {
            if (ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week) add(r.TR_DET, r.TR_AMT, 'shopTrans');
        }

        // 5. Shop payments — itemised: wages, buying, rent, council tax and the rest.
        const shopPay = src.shopPayDetail.filter(r => ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week);
        if (shopPay.length > 0) {
            shopPay.forEach(r => add(
                (r.description && String(r.description).trim() !== '' ? r.description : 'Shop Payment'),
                r.TR_AMT, 'shopPay'));
        } else {
            for (const r of src.shopPaySummed) {
                if (ProfitAndLoss.sameShop(shopId, r.SHOP_ID) && r.TR_WEEK === week) add('Shop Payment', r.TR_AMT, 'shopPay');
            }
        }

        return { lines, total: round2(total), breakdown };
    }

    async handleViewReport() {
        this.props.toggleLoader(true)
        let shopID = "";
        let ReportDetails = []
        for (let i = 0; i < this.state.Shop_Name.length; i++) {
            const element = this.state.Shop_Name[i];
            shopID += element.value + ','
            ReportDetails.push({
                value: element.value,
                label: element.label,
                Day1: "",
                Day2: "",
                repDataList: [],
                reportActData: []
            })
        }

        let filterID = shopID.slice(0, -1);
        let { reportView } = this.state;

        let formData = {
            Shop_Name: filterID,
            From_Week: this.state.From_Week,
            To_Week: this.state.To_Week,
            reportView: reportView
        }

        const getShops = await axios.post(`${process.env.REACT_APP_BASE_URL}services/report/profitAndLoss.php`, formData);
        let shopData = getShops.data
        if (shopData.success === 1 && shopData.message === "Record Available.") {
            let getWSDet_stmt_data = shopData.getWSDet_stmt_data;
            let getShopExp_stmt_data = shopData.getShopExp_stmt_data;
            let getAccPay_stmt_data = shopData.getAccPay_stmt_data;
            let getShopPay_stmt_data = shopData.getShopPay_stmt_data;
            let getShopPayAct_stmt_data = shopData.getShopPayAct_stmt_data;
            let getShopPayment_stmt_data = shopData.getShopPayment_stmt_data || [];
            let getWSDays_stmt_data = shopData.getWSDays_stmt_data || [];
            /* ── ONE expense builder for all three views ──────────────────────────────────
               By Week and Summary used to live here as large commented-out blocks. They were
               NOT revived verbatim: that code predated two fixes now relied on by By Shop —
               charges matched per (shop, week) rather than per shop, and delivery sourced from
               pc_week_del_bd rather than the always-empty pc_shop_trans_cash TR_TYPE='4'. Reusing
               it would have reintroduced the N-times charge over-count. All three views now call
               buildWeekExpenses(), so those rules cannot drift apart again. */
            const expenseSources = {
                charges: getShopExp_stmt_data,
                delivery: getAccPay_stmt_data,
                payAccSummed: getShopPay_stmt_data,
                shopTransCash: getShopPayAct_stmt_data,
                shopPaySummed: getShopPayment_stmt_data,
                payAccDetail: shopData.getShopPayDetail_stmt_data || [],
                shopPayDetail: shopData.getShopPaymentDetail_stmt_data || [],
            };

            // Week label, shared by every view.
            const weekLabel = async (week) => {
                const parts = week.split('-');
                parts[1] = parts[1].replace('W', '');
                return await getISOWeek(...parts.map(n => parseInt(n)).reverse());
            };

            let pushedData = []
            switch (reportView) {

                /* ── SUMMARY: one row per shop for the whole range ────────────────────────────
                   Column set taken from legacy SP_MS_FMS_RptProfitAndLoss_Summary_Query
                   (script.sql:3561), which is also exactly what Summary.js already renders. */
                case 'summary': {
                    const rows = [];
                    for (const shop of this.state.Shop_Name) {
                        /* Key names are Summary.js's existing contract (KnN, other, ProfnLoss),
                           not names of my choosing — the component is reused as-is. */
                        const agg = {
                            ShopName: shop.label, Cash: 0, Card: 0, OtherSale: 0, TotalSale: 0,
                            rentCharges: 0, KnN: 0, Delivery: 0, NDR: 0, other: 0,
                            TotalExp: 0, ProfnLoss: 0,
                            Days: 0, otherLines: [],
                        };
                        let hasSale = false;
                        for (const wsRow of getWSDet_stmt_data) {
                            if (!ProfitAndLoss.sameShop(shop.value, wsRow.PC_WS_SHOPID)) continue;
                            hasSale = true;
                            const { total, breakdown, lines } = this.buildWeekExpenses(shop.value, wsRow.PC_WS_WEEK, expenseSources);
                            /* Days — legacy SP_MS_FMS_RptProfitAndLoss_Summary_Query counts a sale day
                               when cash + card > 0 (T1.36). Display only; no figure uses it. */
                            agg.Days += getWSDays_stmt_data.filter(dd =>
                                String(dd.weekly_sale_id) === String(wsRow.PC_WS_ID)
                                && ((+dd.cash_amount || 0) + (+dd.card_amount || 0)) > 0).length;
                            // The lines behind "Other" (payAcc + shopPay + shopTrans, as summed below).
                            lines.filter(l => ['payAcc', 'shopPay', 'shopTrans'].includes(l.bucket) && (+l.amount || 0) !== 0)
                                .forEach(l => agg.otherLines.push({ week: wsRow.PC_WS_WEEK, description: l.description, amount: l.amount }));
                            agg.Cash += +wsRow.WS_Cash; agg.Card += +wsRow.WS_Card; agg.OtherSale += +wsRow.WS_Other;
                            agg.rentCharges += breakdown.rent; agg.KnN += breakdown.kn; agg.NDR += breakdown.ndr;
                            agg.Delivery += breakdown.delivery;
                            agg.other += breakdown.payAcc + breakdown.shopPay + breakdown.shopTrans;
                            agg.TotalExp += total;
                        }
                        agg.TotalSale = round2(agg.Cash + agg.Card + agg.OtherSale);
                        // Legacy: PnL = (cash + card) - total expense. Sales here include "other",
                        // consistent with the By Shop view.
                        agg.ProfnLoss = round2(agg.TotalSale - agg.TotalExp);
                        ['Cash','Card','OtherSale','rentCharges','KnN','NDR','Delivery','other','TotalExp']
                            .forEach(k => { agg[k] = round2(agg[k]); });
                        /* As the legacy report (an INNER JOIN on weekly sales): a shop with no approved
                           sale in the range is not listed. Its row was all zeros, so no total moves
                           (user's choice, 2026-10-26, T1.36). */
                        if (hasSale) rows.push(agg);
                    }
                    // Summary.js reads ReportDetails.reportActData, plus Day1/Day2 for the heading.
                    pushedData = {
                        Day1: this.state.From_Week,
                        Day2: this.state.To_Week,
                        reportActData: rows,
                    };
                    break;
                }

                /* ── BY WEEK: a block per week, each listing every selected shop ─────────────── */
                case 'by_week': {
                    const weeks = [];
                    for (const wsRow of getWSDet_stmt_data) {
                        if (!weeks.some(w => w.WeekNumberB === wsRow.PC_WS_WEEK)) {
                            weeks.push({ WeekNumberB: wsRow.PC_WS_WEEK, ToFromDate: '', shopsForReport: [] });
                        }
                    }
                    /* A week whose weekly sale is not approved has no row above, so it would not
                       appear at all. Add it as an EMPTY week (no shops, so no figures) so the
                       "Not approved" marker has a line to sit on (T1.35, user's choice). */
                    for (const u of (shopData.unapprovedWeeks || [])) {
                        if (!weeks.some(w => normalizeWeekCode(w.WeekNumberB) === normalizeWeekCode(u.WEEK))) {
                            weeks.push({ WeekNumberB: u.WEEK, ToFromDate: '', shopsForReport: [] });
                        }
                    }
                    // Chronological, and tolerant of a non-padded "2026-W9" among padded codes.
                    const weekKey = (w) => {
                        const [y, n] = w.split('-W');
                        return parseInt(y, 10) * 100 + parseInt(n, 10);
                    };
                    weeks.sort((a, b) => weekKey(a.WeekNumberB) - weekKey(b.WeekNumberB));

                    for (const week of weeks) {
                        week.ToFromDate = await weekLabel(week.WeekNumberB);
                        for (const shop of this.state.Shop_Name) {
                            const wsRow = getWSDet_stmt_data.find(
                                r => ProfitAndLoss.sameShop(r.PC_WS_SHOPID, shop.value) && r.PC_WS_WEEK === week.WeekNumberB);
                            if (!wsRow) continue;   // that shop filed no approved sale that week
                            const { lines: expLines, total } = this.buildWeekExpenses(shop.value, week.WeekNumberB, expenseSources);
                            week.shopsForReport.push({
                                ShopName: shop.label,
                                ToFromDate: week.ToFromDate,
                                WS_Cash: wsRow.WS_Cash,
                                WS_Card: wsRow.WS_Card,
                                WS_Other: wsRow.WS_Other,
                                WS_OtherExp: total,
                                WS_PRO_LOS: round2((+wsRow.WS_Cash + +wsRow.WS_Card + +wsRow.WS_Other) - total),
                                WeeklySale: getWSDays_stmt_data
                                    .filter(d => String(d.weekly_sale_id) === String(wsRow.PC_WS_ID))
                                    .map(d => ({ DateAndDay: d.sale_date, Cash: d.cash_amount, Card: d.card_amount, Others: d.other_amount })),
                                WeeklySaleExpense: expLines,
                            });
                        }
                    }
                    pushedData = { Weeks: weeks };
                    break;
                }

                /* ── BY SHOP: a block per shop, each listing its weeks (unchanged in shape) ──── */
                case 'by_shop': {
                    const pNlRepData = [];
                    for (const shop of this.state.Shop_Name) {
                        const entry = { shopName: shop.label, dataForReport: [] };
                        for (const wsRow of getWSDet_stmt_data) {
                            if (!ProfitAndLoss.sameShop(shop.value, wsRow.PC_WS_SHOPID)) continue;
                            const { lines: expLines, total } = this.buildWeekExpenses(shop.value, wsRow.PC_WS_WEEK, expenseSources);
                            entry.dataForReport.push({
                                ToFromDate: await weekLabel(wsRow.PC_WS_WEEK),
                                weekCode: wsRow.PC_WS_WEEK,
                                WS_Cash: wsRow.WS_Cash,
                                WS_Card: wsRow.WS_Card,
                                WS_Other: wsRow.WS_Other,
                                WS_OtherExp: total,
                                WS_PRO_LOS: round2((+wsRow.WS_Cash + +wsRow.WS_Card + +wsRow.WS_Other) - total),
                                WeeklySale: getWSDays_stmt_data
                                    .filter(d => String(d.weekly_sale_id) === String(wsRow.PC_WS_ID))
                                    .map(d => ({ DateAndDay: d.sale_date, Cash: d.cash_amount, Card: d.card_amount, Others: d.other_amount })),
                                WeeklySaleExpense: expLines,
                            });
                        }
                        /* Same as By Week: a week with not-approved records but no approved sale
                           for this shop gets an EMPTY entry carrying only its label, so the
                           marker shows (T1.35). No figures are added. */
                        for (const u of (shopData.unapprovedWeeks || [])) {
                            if (entry.dataForReport.some(r => normalizeWeekCode(r.weekCode) === normalizeWeekCode(u.WEEK))) continue;
                            entry.dataForReport.push({ ToFromDate: await weekLabel(u.WEEK), weekCode: u.WEEK, noApprovedSale: true });
                        }
                        entry.dataForReport.sort((a, b) => normalizeWeekCode(a.weekCode).localeCompare(normalizeWeekCode(b.weekCode)));
                        pNlRepData.push(entry);
                    }
                    pushedData = pNlRepData;
                    break;
                }

                default:
                    break;
            }
            // Weeks in the range holding not-approved records, system-wide — a marker only (T1.35).
            this.setState({ showReport: true, ReportDetails: pushedData, unapprovedWeeks: shopData.unapprovedWeeks || [] })

        }

        this.props.toggleLoader(false)

    }

    render() {
        let { shopPaymentDays, reportView, showReport } = this.state;
        return (
            <Container>
                <PageHeader breadcrumb="section" title="Profit & Loss"
                    right={<PrintButton content={() => this.newRef} disabled={this.state.showReport !== true} />} />
                <FilterCard>
                    <Row>
                        <Col style={{ textAlign: "center" }} >
                            <FormControl>
                                <RadioGroup
                                    row
                                    aria-labelledby="reportView-label"
                                    name="reportView"
                                    value={this.state.reportView}
                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                                >
                                    <FormControlLabel value="by_shop" control={<Radio />} label="By Shop" onChange={this.handleChange.bind(this, 'reportView')} />
                                    <FormControlLabel value="by_week" control={<Radio />} label="By Week" onChange={this.handleChange.bind(this, 'reportView')} />
                                    <FormControlLabel value="summary" control={<Radio />} label="Summary" onChange={this.handleChange.bind(this, 'reportView')} />
                                </RadioGroup>
                            </FormControl>
    
    
                        </Col>
                    </Row>
                    <Row style={{ alignItems: 'center' }} >
                        <Col md={3} >
                            <Form.Group className="mb-3" >
                                <Form.Label>Shop</Form.Label>
                                <Select menuPortalTarget={document.body}
                                    name="Shop_Name"
                                    menuPlacement="auto"
                                    isClearable={!this.state.shopLocked}
                                    isDisabled={!!this.state.shopLocked}
                                    isMulti
                                    style={{ height: '32px' }}
                                    value={this.state.Shop_Name}
                                    options={this.state.ShopList}
                                    onChange={this.handleChange.bind(this, 'Shop_Name')}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} >
                            <Form.Group className="mb-3" >
                                <Form.Label
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        gap: '8px',
                                        /*
                                         * The date range on the right is EMPTY until a week is picked.
                                         * Without nowrap and a reserved line height it appeared on
                                         * selection, wrapped to a second line, and shoved the picker
                                         * down — the whole filter row visibly jumped the moment you
                                         * chose a week. Reserve the line, never wrap, truncate instead.
                                         */
                                        whiteSpace: 'nowrap',
                                        minHeight: '24px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <span style={{ flexShrink: 0 }}>From Week</span>
                                    <span
                                        style={{
                                            flexShrink: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontWeight: 400,
                                            opacity: 0.75
                                        }}
                                    >{shopPaymentDays.length > 0 ? `(${shopPaymentDays[0]['DateAndDay']} - ${shopPaymentDays[6]['DateAndDay']})` : ``}</span>
                                </Form.Label>
                                <WeekRangeSelect
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    value={this.state.From_Week}
                                    onChange={this.handleChange.bind(this, "From_Week")}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} >
                            <Form.Group className="mb-3" >
                                <Form.Label
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        gap: '8px',
                                        /*
                                         * The date range on the right is EMPTY until a week is picked.
                                         * Without nowrap and a reserved line height it appeared on
                                         * selection, wrapped to a second line, and shoved the picker
                                         * down — the whole filter row visibly jumped the moment you
                                         * chose a week. Reserve the line, never wrap, truncate instead.
                                         */
                                        whiteSpace: 'nowrap',
                                        minHeight: '24px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <span style={{ flexShrink: 0 }}>To Week</span>
                                    <span
                                        style={{
                                            flexShrink: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontWeight: 400,
                                            opacity: 0.75
                                        }}
                                    >{shopPaymentDays.length > 0 ? `(${shopPaymentDays[0]['DateAndDay']} - ${shopPaymentDays[6]['DateAndDay']})` : ``}</span>
                                </Form.Label>
                                <WeekRangeSelect
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    value={this.state.To_Week}
                                    onChange={this.handleChange.bind(this, "To_Week")}
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Button className="add-button" size="small" variant="contained" onClick={this.handleViewReport.bind(this)} >View</Button>
                        </Col>
                    </Row>
                </FilterCard>
                <div id="ProfitAndLost" >
                    {
                        reportView === 'summary' && showReport === true ?
                            <Summary ref={el => (this.newRef = el)} ReportDetails={this.state.ReportDetails} UnapprovedWeeks={this.state.unapprovedWeeks} />
                            :
                            reportView === 'by_week' && showReport === true ?
                                <ByWeek ref={el => (this.newRef = el)} ReportDetails={this.state.ReportDetails} UnapprovedWeeks={this.state.unapprovedWeeks} />
                                :
                                reportView === 'by_shop' && showReport === true ?
                                    <ByShop ref={el => (this.newRef = el)} ReportDetails={this.state.ReportDetails} UnapprovedWeeks={this.state.unapprovedWeeks} />
                                    :
                                    ""
                    }
                </div>
            </Container>
        );
    }
}

export default ProfitAndLoss;